import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import type { TSteps, ISmsResponse, IFinishResponse } from '../../models';
import { sentryLogError, getVerifySmsApi } from '../../utils';

import {
    setStep,
    setStatus,
    setErrorStep,
    setChangePhoneButtonVisible,
    setUserStatus,
    setLastDtPathStatus,
} from './application';
import { setCashbackApplyStatus } from './first-step';
import { handleFinishOrder } from './finish';
import { draftCheckRequest } from './draft-check';
import { setDraftCookie } from './draft';
import type { ISmsSlice, TCreateThunkApi } from './models';

export const successMapping: Record<Exclude<ISmsResponse['status'], 'SMS_ERROR'>, TSteps> = {
    NEED_DUL: 'passport',
    SUCCESS: 'delivery',
    NODT: 'delivery',
    REFER_TO_OFFICE: 'error',
    REJECT_COMPLETELY: 'error',
    DT_CLIENT: 'error',
    NO_OFFER: 'error',
};

const initialState: ISmsSlice = {
    state: 'INITIAL',
    screen: 'INITIAL',
    attemptsCount: 0,
    maxAttemps: 3,
};

export const smsSlice = createSlice({
    name: 'sms',
    initialState,
    reducers: {
        changeState: (state, action: PayloadAction<ISmsSlice['state']>) => {
            state.state = action.payload;
        },
        changeScreen: (state, action: PayloadAction<ISmsSlice['screen']>) => {
            state.screen = action.payload;
        },
        setAttemptsCount: (state, action: PayloadAction<ISmsSlice['attemptsCount']>) => {
            state.attemptsCount = action.payload;
        },
        setInitAttempts: (state, action: PayloadAction<ISmsSlice['maxAttemps']>) => {
            state.maxAttemps = action.payload;
            state.attemptsCount = action.payload;
        },
        resetSms: state => {
            state.state = 'INITIAL';
            state.screen = 'INITIAL';
            state.attemptsCount = state.maxAttemps;
        },
    },
});

export const { changeScreen, changeState, resetSms, setAttemptsCount, setInitAttempts } =
    smsSlice.actions;

export const smsConfirmRequest = createAsyncThunk<string | undefined, string, TCreateThunkApi>(
    'smsConfirmRequest',
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (code, { dispatch, getState }) => {
        dispatch(changeState('CODE_CHECKING'));

        const {
            application: { appId, businessUid, gentleErrorEnabled, status: appStatus },
            sms: { attemptsCount },
        } = getState();

        const { verifySmsApi } = getVerifySmsApi(appStatus);

        try {
            const { data } = await verifySmsApi({ uuid: businessUid, code, appId });
            const { lastDtPathStatus, status: resStatus } = data;

            dispatch(setLastDtPathStatus(lastDtPathStatus));

            if (appStatus === 'NEED_SMS' && resStatus === 'SUCCESS') {
                const finishData = data as IFinishResponse;

                dispatch(handleFinishOrder(finishData));

                return resStatus;
            }

            const verifySmsResStatus = resStatus as ISmsResponse['status'];
            const verifySmsData = data as ISmsResponse;

            if (verifySmsResStatus === 'SMS_ERROR') {
                const newAttemptsCount = attemptsCount - 1;

                dispatch(setAttemptsCount(newAttemptsCount));

                if (newAttemptsCount <= 0) {
                    dispatch(changeScreen('FATAL_ERROR'));
                } else {
                    dispatch(changeState('CODE_ERROR'));
                }

                return verifySmsResStatus;
            }

            if (successMapping[verifySmsResStatus]) {
                const resStatusSuccesOrNODT = resStatus === 'SUCCESS' || resStatus === 'NODT';
                let nextStep = successMapping[verifySmsResStatus];
                if (gentleErrorEnabled && resStatusSuccesOrNODT) {
                    nextStep = 'gentleErrorPage';
                }
                dispatch(setStep(nextStep));
                dispatch(resetSms());
                dispatch(setStatus(resStatus));
                if (verifySmsData?.payload?.userStatus) {
                    dispatch(setUserStatus(verifySmsData.payload.userStatus));
                }

                if (resStatusSuccesOrNODT) {
                    dispatch(setChangePhoneButtonVisible(false));
                    dispatch(draftCheckRequest());

                    dispatch(setDraftCookie(verifySmsData.draftId));

                    if ('cashbackStatus' in data && data.cashbackStatus) {
                        dispatch(setCashbackApplyStatus(data.cashbackStatus));
                    }
                }

                return resStatus;
            }

            sentryLogError({
                message: `applicationsVerifySms вернул ошибку со статусом: ${verifySmsResStatus}`,
                data: { appId, businessUid },
            });
            dispatch(setErrorStep());
        } catch (error) {
            sentryLogError({
                error,
                message: 'applicationsVerifySms error',
                data: { appId, businessUid },
            });

            if (!axios.isAxiosError(error)) {
                dispatch(setErrorStep());

                return;
            }

            dispatch(setErrorStep(error?.response?.data?.status));
        }
    }
);

export const smsResend = createAsyncThunk<void, undefined, TCreateThunkApi>(
    'smsResend',
    (_, { dispatch, getState }) => {
        const {
            application: { appId, businessUid, status: appStatus },
        } = getState();
        dispatch(changeState('CODE_SENDING'));

        const { requestSmsCodeApi } = getVerifySmsApi(appStatus);

        requestSmsCodeApi({ uuid: businessUid, appId })
            .then(() => dispatch(resetSms()))
            .catch(error => {
                sentryLogError({
                    error,
                    message: 'requestSmsCode error',
                    data: { appId, businessUid },
                });

                if (!axios.isAxiosError(error)) {
                    dispatch(setErrorStep());

                    return;
                }

                dispatch(setErrorStep(error?.response?.data?.status));
            });
    }
);
